import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, FlaskConical, AlertTriangle, BookOpen, ExternalLink, ArrowUp, Shield, GitCompareArrows, Check, X as XIcon, Link2, Download, ClipboardCheck } from 'lucide-react';
import { setProgressForSlug } from '../../pages/GuidesHubPage';
import RelatedGuides from './RelatedGuides';
import AprTrustBlock from '../apr/AprTrustBlock';

const ARTICLE_SLUG = 'peptide-side-effects-what-the-research-actually-says';

// ── COMPARE METADATA ──
const COMPARE_DATA = {
  bpc157: {
    category: 'Repair & Healing',
    evidenceLevel: 'Moderate',
    evidenceDetail: '30+ years preclinical, 3 human pilot studies, 0 serious adverse events',
    topEffects: ['Mild headaches', 'Temporary nausea', 'Injection site redness'],
    effectSeverity: 'Very Mild',
    effectRate: '~10-15%',
    titration: 'Start at 125-250 mcg 2x/day, increase to 500 mcg if tolerated',
    adaptationWindow: '3-7 days',
    keyRisk: 'Product purity (no inherent organ toxicity)',
    bestFor: 'Gut healing, tendon/ligament repair',
  },
  tb500: {
    category: 'Tissue Repair',
    evidenceLevel: 'Moderate',
    evidenceDetail: 'Extensive preclinical, Thymosin Beta-4 well-studied, TB-500 specific trials limited',
    topEffects: ['Fatigue during loading', 'Mild lethargy', 'Increased thirst'],
    effectSeverity: 'Mild',
    effectRate: '~30-55%',
    titration: 'Loading: 2-5 mg/week for 4-6 wks, then 2 mg/week maintenance',
    adaptationWindow: '2-3 weeks',
    keyRisk: 'Fatigue during loading phase (expected, not harmful)',
    bestFor: 'Widespread soft tissue repair, flexibility',
  },
  ghkcu: {
    category: 'Anti-Aging & Skin',
    evidenceLevel: 'Strong',
    evidenceDetail: 'Endogenous peptide, 12-week human clinical trial (n=71), modulates 4,000+ genes',
    topEffects: ['Topical: mild redness', 'Skin dryness (turnover)', 'Injectable: site irritation'],
    effectSeverity: 'Very Mild',
    effectRate: '~5-10%',
    titration: 'Topical: 2x/day. Injectable: 1-2 mg/day SubQ',
    adaptationWindow: '3-5 days',
    keyRisk: 'Copper sensitivity (rare)',
    bestFor: 'Collagen, skin repair, wound healing',
  },
  cjc: {
    category: 'Growth Hormone',
    evidenceLevel: 'Strong',
    evidenceDetail: 'RCT-proven (2006), dose-dependent GH 2-10x, IGF-1 1.5-3x, no serious AEs',
    topEffects: ['Water retention', 'Tingling/warmth', 'Increased hunger'],
    effectSeverity: 'Moderate',
    effectRate: '~15-30%',
    titration: 'Start 100/100 mcg before bed, increase to 300/300 mcg over 2-4 wks',
    adaptationWindow: '1-2 weeks',
    keyRisk: 'Water retention and numbness at higher doses',
    bestFor: 'GH optimization, body composition, sleep',
  },
  aod: {
    category: 'Fat Loss',
    evidenceLevel: 'Very Strong',
    evidenceDetail: '6 human RCTs, 893+ participants, tolerability = placebo, GRAS designation',
    topEffects: ['Mild nausea (fasted)', 'Increased thirst', 'Mild hunger'],
    effectSeverity: 'Very Mild',
    effectRate: '~5-10%',
    titration: '300-500 mcg/day fasted, no titration needed',
    adaptationWindow: '3-7 days',
    keyRisk: 'Minimal — most studied peptide on this list',
    bestFor: 'Targeted fat loss without GH side effects',
  },
  retatrutide: {
    category: 'Weight Management',
    evidenceLevel: 'Strong',
    evidenceDetail: 'Phase 2 RCT (n=338, NEJM 2023), 24.2% weight loss at 48 wks, Phase 3 ongoing',
    topEffects: ['Nausea (~45%)', 'Diarrhea (~30%)', 'Vomiting (~20%)'],
    effectSeverity: 'Moderate-High',
    effectRate: '~40-60%',
    titration: '0.5 mg/wk, increase 0.5-1 mg every 4 wks over 12-16 wks',
    adaptationWindow: '4-8 weeks',
    keyRisk: 'GI events during titration, monitor pancreatic enzymes',
    bestFor: 'Maximum fat loss, metabolic improvement',
  },
};

const EVIDENCE_COLORS = {
  'Very Strong': '#22c55e',
  'Strong': '#22c55e',
  'Moderate': '#eab308',
  'Limited': '#f97316',
};

const SEVERITY_RANK = {
  'Very Mild': { color: '#22c55e', pct: 15 },
  'Mild': { color: '#84cc16', pct: 30 },
  'Moderate': { color: '#eab308', pct: 55 },
  'Moderate-High': { color: '#f97316', pct: 75 },
  'High': { color: '#ef4444', pct: 90 },
};

// ── DATA ──
const PEPTIDES = [
  {
    id: 'bpc157', name: 'BPC-157', highlight: '157', label: 'Body Protection Compound · Repair & Gut Healing',
    intro: 'Body Protection Compound-157 is a pentadecapeptide (15 amino acids) derived from a protective protein found in human gastric juice. It has over three decades of preclinical study behind it and is one of the most-researched healing peptides in the space.',
    reactions: [
      { label: 'Common', text: 'Mild headaches — often dose-related, resolve within a few days' },
      { label: 'Common', text: 'Temporary nausea, particularly early in use or with oral forms' },
      { label: 'Common', text: 'Increased appetite during healing phases' },
      { label: 'Common', text: 'Injection site redness or minor irritation' },
      { label: 'Rare', text: 'Dizziness or flushing — particularly after injection', rare: true },
      { label: 'Rare', text: 'Sleep disturbances — if dosed late in the day', rare: true },
    ],
    citations: [
      { icon: '🔬', text: 'A 2025 PMC-indexed narrative review of three available human trials found no reported side effects in an intravesicular BPC-157 pilot study (Lee et al., 2024), with participants specifically screened for fevers, skin rash, nausea, vomiting, and worsening symptoms.', source: 'Source: PMC12446177 — Regeneration or Risk? A Narrative Review of BPC-157 (2025)' },
      { icon: '📄', text: 'A 2025 review in Pharmaceuticals (MDPI) confirmed that BPC-157 has undergone hundreds of preclinical experiments over 30+ years with no organ toxicity, carcinogenic effects, or established lethal dose.', source: 'Source: MDPI Pharmaceuticals 18(2):185 — Multifunctionality of BPC-157 (2025)' },
    ],
    normal: { title: "What's Normal vs. What Isn't", text: 'Normal: Mild headache during week 1, slight nausea with oral forms, minor injection site redness. These typically resolve within days.\n\nNot Normal: Headaches persisting beyond 2 weeks, severe nausea, significant swelling or infection at injection sites. If these occur, reduce dose or stop and consult a provider.' },
    extraNote: 'The purity problem: A significant percentage of BPC-157 "side effects" in community reports are likely attributable to product contamination rather than the peptide itself.',
    cta: { text: 'Research-Grade BPC-157 via APR', sub: 'American Peptide Research — third-party tested, COA available.', url: 'https://www.americanpeptideresearch.net/product/bpc-157-10mg/ref/16/', btnText: 'View BPC-157' },
    severity: null,
    articleLink: '/blogs/article/blood-tests-before-peptides-the-complete-pre-protocol-lab-guide',
  },
  {
    id: 'tb500', name: 'TB-500', highlight: '500', label: 'Thymosin Beta-4 Fragment · Tissue Repair & Flexibility',
    intro: "TB-500 is a synthetic analog of Thymosin Beta-4, specifically the active actin-binding domain of the naturally occurring 43-amino acid protein. It travels throughout the body rather than acting locally, making it effective for widespread soft tissue injury recovery.",
    reactions: [
      { label: 'Common — Loading Phase', text: 'Fatigue or lethargy — particularly in week 1, as the body prioritizes tissue repair' },
      { label: 'Common', text: 'Mild lethargy after dosing — linked to systemic immune modulation' },
      { label: 'Common', text: 'Increased thirst during the loading phase' },
      { label: 'Common', text: 'Injection site redness or mild discomfort' },
      { label: 'Rare', text: 'Muscle tightness or light cramping — at higher loading doses', rare: true },
      { label: 'Rare', text: 'Transient headache — resolves within hours of administration', rare: true },
    ],
    citations: [
      { icon: '🔬', text: "PeptideDeck's 2026 clinical review notes that fatigue during TB-500's loading phase is linked to systemic immune modulation — not toxicity. Nausea is rare and observed primarily at loading doses ≥6 mg/week.", source: 'Source: PeptideDeck TB-500 Dosage Guide (2026)' },
      { icon: '📄', text: 'Thymosin Beta-4 downregulates pro-inflammatory cytokines including IL-6 and TNF-α. Fatigue during early use is consistent with the body undergoing genuine tissue repair.', source: 'Source: Smart et al., Circulation Research; Philp et al., JOID; Maar et al., Cells (2021)' },
    ],
    normal: { title: 'The Adaptation Phase — Why This Is a Good Sign', text: "TB-500's fatigue and increased thirst during the loading phase aren't random side effects — they reflect the compound doing exactly what it's supposed to do. Most users find energy levels normalize or improve by week 3." },
    severity: [
      { label: 'Fatigue', pct: 55 },
      { label: 'Injection Site', pct: 35 },
      { label: 'Headache', pct: 15 },
      { label: 'Cramping', pct: 8, red: true },
    ],
    cta: { text: 'Research-Grade TB-500 via APR', sub: 'Third-party tested, COA available.', url: 'https://www.americanpeptideresearch.net/product/tb-500-10mg/ref/16/', btnText: 'View TB-500' },
    articleLink: '/blogs/article/why-mixing-peptides-sabotages-results',
  },
  {
    id: 'ghkcu', name: 'GHK-Cu', highlight: 'Cu', label: 'Copper Tripeptide · Collagen, Skin & Systemic Repair',
    intro: "GHK-Cu is a naturally occurring tripeptide found in human plasma. Unlike most research peptides, it has a confirmed human endogenous origin. Plasma levels decline from ~200 ng/mL at age 20 to ~80 ng/mL by age 60, correlating with decreased regenerative capacity.",
    reactions: [
      { label: 'Topical — 5–10% of Users', text: 'Redness or tingling at application site — especially sensitive skin types' },
      { label: 'Topical', text: 'Temporary skin dryness during collagen turnover' },
      { label: 'Injectable', text: 'Mild irritation at injection site — resolve with site rotation' },
      { label: 'Rare — Copper-Sensitive', text: 'Mild headaches, typically injectable only, resolves within hours', rare: true },
      { label: 'Rare', text: 'Histamine response: mild flushing or itching — indicates dose reduction needed', rare: true },
    ],
    citations: [
      { icon: '🔬', text: 'A 2016 Scientific Reports study tested GHK-Cu\'s skin irritation potential: GHK-Cu did not show any signs of irritation in contrast to free copper compounds.', source: 'Source: Scientific Reports 37664 — Selected Biomarkers Revealed Potential Skin Toxicity (2016)' },
      { icon: '📄', text: 'Pickart et al. established that the peptide modulates over 4,000 genes. In a 12-week clinical trial of 71 women, GHK-Cu cream improved collagen production in 70% of participants — outperforming both vitamin C cream and retinoic acid.', source: 'Source: PMC6073405 — Regenerative Actions of GHK-Cu (2018)' },
    ],
    normal: { title: 'The Skin Dryness Paradox', text: "Users sometimes report temporary skin dryness when starting topical GHK-Cu. In most cases, it reflects active collagen turnover. Applying GHK-Cu to slightly damp skin and following with a hydrating serum eliminates this." },
    cta: { text: 'Research-Grade GHK-Cu via APR', sub: 'Verified purity, COA documentation available.', url: 'https://www.americanpeptideresearch.net/ref/16/', btnText: 'View GHK-Cu' },
    articleLink: '/blogs/article/ghk-cu-copper-peptide-the-complete-master-class-guide-2026',
  },
  {
    id: 'cjc', name: 'CJC-1295 + Ipamorelin', highlight: 'Ipamorelin', label: 'GHRH Analog + Ghrelin Mimetic · Growth Hormone Optimization',
    intro: "The most commonly prescribed growth hormone peptide stack in clinical longevity medicine. CJC-1295 provides sustained GH/IGF-1 elevation, while Ipamorelin delivers a clean, selective GH pulse. The side effect profile is more systemic than repair peptides.",
    reactions: [
      { label: 'Common — 15–25%', text: 'Water retention — mild puffiness in hands, feet, or face during first 2–4 weeks' },
      { label: 'Common', text: 'Tingling or warmth in the face or scalp — GH pulse response' },
      { label: 'Common — 10–15%', text: 'Temporary hunger increase — from ghrelin receptor stimulation' },
      { label: 'Common', text: 'Mild tiredness after dosing — aligns with GH pulse timing' },
      { label: 'Rare — Higher Doses', text: 'Finger numbness on waking — GH-induced fluid compressing peripheral nerves', rare: true },
      { label: 'Rare', text: 'Mild flushing or warmth post-injection — vasodilatory effect', rare: true },
    ],
    citations: [
      { icon: '🔬', text: 'The landmark 2006 RCT found subcutaneous CJC-1295 produced dose-dependent GH increases of 2–10× for 6+ days and IGF-1 increases of 1.5–3× for 9–11 days. No serious adverse reactions were reported across any dose group.', source: 'Source: PubMed 16352683 — Prolonged stimulation of GH/IGF-I by CJC-1295 (2006)' },
      { icon: '📄', text: 'Aggregated data: injection site reactions 20–30%, water retention 15–25%, tingling/numbness 10–20%, increased hunger 10–15%, fatigue 10–15%, headache 5–10%. All dose-dependent, subside within 1–2 weeks.', source: 'Source: FormBlends — CJC-1295/Ipamorelin Side Effects Guide (2026)' },
    ],
    normal: { title: 'Timing Trick That Reduces Side Effects', text: "Administering CJC-1295 + Ipamorelin 60–90 minutes after your last meal, before bed aligns with the body's natural nocturnal GH pulse. You sleep through the tingling and hunger spike — and the GH pulse aligns with deep sleep stages." },
    cta: { text: 'CJC-1295 + Ipamorelin via APR', sub: 'Research-grade, COA available.', url: 'https://www.americanpeptideresearch.net/ref/16/', btnText: 'Shop APR' },
    articleLink: '/blogs/article/cjc-1295-with-dac-ipamorelin-how-these-2-peptides-rebuild-your-body-from-the-inside-out',
  },
  {
    id: 'aod', name: 'AOD-9604', highlight: '9604', label: 'hGH C-Terminal Fragment · Fat Metabolism & Metabolic Health',
    intro: "AOD-9604 has the most extensive human clinical safety data of any peptide on this list — six randomized, double-blind, placebo-controlled trials involving over 893 participants. Its safety profile is remarkably clean and well-documented.",
    reactions: [
      { label: 'Common', text: 'Mild nausea when taken on an empty stomach — transient' },
      { label: 'Common', text: 'Increased thirst — metabolic shifts increase cellular hydration demands' },
      { label: 'Some Users', text: 'Hunger increases — though AOD-9604 does not significantly affect ghrelin' },
      { label: 'Rare — Early Use', text: 'Restlessness or mild agitation during early metabolic adjustment', rare: true },
      { label: 'Common — Injection', text: 'Localized tissue reaction at administration site' },
      { label: 'Rare', text: 'Mild transient headache — typically week 1–2 only', rare: true },
    ],
    citations: [
      { icon: '🔬', text: 'Stier et al. (2013) summarized all six human RCTs: AOD9604 displayed a tolerability profile indistinguishable from placebo. No anti-AOD9604 antibodies were detected. No effect on IGF-1, insulin resistance, or glucose metabolism.', source: 'Source: J Endocrinology & Metabolism — Safety of AOD9604 in Humans (2013)' },
      { icon: '📄', text: 'Moré et al. (2014): chronic toxicology in rats (6 months) and monkeys (9 months) showed no genotoxic activity. AOD9604 was designated Generally Recognized As Safe (GRAS).', source: 'Source: J Endocrinology & Metabolism — Safety and Metabolism of AOD9604 (2014)' },
    ],
    normal: { title: 'Short-Term Metabolic Responses — Expected, Not Alarming', text: "Nausea and thirst in weeks 1–2 are short-term metabolic responses — the body adjusting to enhanced lipolytic activity. Taking AOD-9604 with a small amount of food eliminates most nausea." },
    cta: { text: 'AOD-9604 via APR', sub: 'Research-grade, third-party tested.', url: 'https://www.americanpeptideresearch.net/ref/16/', btnText: 'View AOD-9604' },
    articleLink: '/blogs/article/aod-9604-fat-burning-peptide-guide',
  },
  {
    id: 'retatrutide', name: 'Retatrutide', highlight: 'rutide', label: 'GLP-1/GIP/Glucagon Triple Agonist · Weight Management & Body Composition',
    intro: "Retatrutide (LY3437943) is a first-in-class triple hormone receptor agonist — simultaneously activating GLP-1, GIP, and glucagon receptors. Its Phase 2 clinical trial (2023) produced the most dramatic weight loss results ever documented for an incretin-based therapy: up to 24.2% body weight reduction at 48 weeks. It represents a fundamentally different mechanism than single-agonist GLP-1 drugs like semaglutide.",
    reactions: [
      { label: 'Very Common — 40–60%', text: 'Nausea — the most frequent side effect, peaks during dose escalation and typically diminishes over 4–8 weeks' },
      { label: 'Common — 20–35%', text: 'Diarrhea — more prevalent than with semaglutide, likely due to glucagon receptor activation affecting gut motility' },
      { label: 'Common — 15–25%', text: 'Vomiting — dose-dependent, most common during titration phases' },
      { label: 'Common — 10–20%', text: 'Decreased appetite — expected pharmacological effect, not a true adverse reaction' },
      { label: 'Common', text: 'Constipation — GLP-1 mediated gastric slowing, alternates with diarrhea in some users' },
      { label: 'Less Common', text: 'Injection site reactions — erythema, pruritus at administration site' },
      { label: 'Rare — Monitor', text: 'Increased heart rate — small mean increases of 2–4 bpm observed at higher doses', rare: true },
      { label: 'Rare — Dose-Dependent', text: 'Elevated lipase/amylase — pancreatic enzyme elevations without clinical pancreatitis', rare: true },
    ],
    citations: [
      { icon: '🔬', text: 'Jastreboff et al. (2023, NEJM): The Phase 2 trial enrolled 338 adults with obesity. At the highest dose (12 mg), participants lost a mean of 24.2% body weight at 48 weeks vs. 2.1% with placebo. GI side effects were the most common, with nausea affecting ~45% of participants during dose escalation, but most events were mild to moderate.', source: 'Source: NEJM 389;6 — Triple-Hormone-Receptor Agonist Retatrutide for Obesity (2023)' },
      { icon: '📄', text: 'The glucagon receptor component drives increased hepatic lipid oxidation and energy expenditure — a mechanism absent in GLP-1-only drugs. This may explain both the superior efficacy and the distinct GI side effect profile (particularly diarrhea). Dose titration over 12–16 weeks significantly reduced discontinuation rates.', source: 'Source: Lancet 402 — Retatrutide Phase 2 Safety & Efficacy Analysis (2023)' },
      { icon: '🔬', text: 'Separate Phase 2 data in Type 2 Diabetes (Rosenstock et al., 2023): Retatrutide improved HbA1c by up to 2.16% and reduced body weight by 16.9% at 36 weeks. The safety profile was consistent with the obesity trial — predominantly GI adverse events during titration.', source: 'Source: Lancet — Efficacy and safety of retatrutide in T2D (2023)' },
    ],
    severity: [
      { label: 'Nausea', pct: 50 },
      { label: 'Diarrhea', pct: 30 },
      { label: 'Vomiting', pct: 20 },
      { label: 'Constipation', pct: 15 },
      { label: 'Heart Rate +', pct: 5, red: true },
    ],
    normal: { title: 'The Titration Window — Why Slow Escalation Is Non-Negotiable', text: "The majority of GI side effects occur during dose escalation. In the Phase 2 trial, participants who titrated slowly over 12–16 weeks had significantly fewer discontinuations than rapid-escalation groups. Starting at 0.5 mg/week and increasing by 0.5–1 mg every 4 weeks is the approach most consistent with manageable side effects.\n\nImportant: Retatrutide's glucagon component means it has a different GI profile than semaglutide. Diarrhea is more common (vs. constipation with GLP-1-only drugs), and the appetite suppression is more pronounced. These are pharmacological effects, not toxicity." },
    extraNote: 'Not yet FDA-approved: Retatrutide is in Phase 3 clinical trials as of 2026. All data referenced is from Phase 2 trials. Its side effect profile may be further refined as larger trial populations are studied.',
    cta: { text: 'Learn More About Retatrutide', sub: 'Research compound — Phase 3 trials ongoing.', url: 'https://www.americanpeptideresearch.net/product/reta-30mg/ref/16/', btnText: 'Shop APR' },
  },
];

const WHY_REASONS = [
  { num: '01', title: 'Dosing Too High', text: 'The majority of headaches, nausea, and water retention are dose-dependent. Most protocols recommend starting at 50% of target dose.' },
  { num: '02', title: 'Low Purity Products', text: 'Bacterial endotoxins and synthesis byproducts from low-quality peptides cause inflammatory reactions incorrectly attributed to the compound.' },
  { num: '03', title: 'Individual Sensitivity', text: 'Copper sensitivity (GHK-Cu), histamine reactivity, and immune sensitivity vary by individual.' },
  { num: '04', title: 'Delivery Irritation', text: 'Injection technique matters — blunt needles, same-site repeats, and improper reconstitution cause local reactions.' },
  { num: '05', title: 'Normal Biological Adaptation', text: "Fatigue during TB-500 loading, skin dryness during GHK-Cu collagen turnover, and hunger with GH peptides are adaptation signals." },
];

const FAQS = [
  { q: 'Are peptide side effects dangerous?', a: 'Most peptide side effects are mild, dose-related, and temporary. Serious adverse events are rare across clinical literature. AOD-9604 had zero serious adverse events across 6 RCTs with 893+ participants.' },
  { q: 'Which peptide has the fewest side effects?', a: 'AOD-9604 has the most robust human clinical safety data — tolerability indistinguishable from placebo. BPC-157 and GHK-Cu also show very favorable profiles.' },
  { q: 'Why do peptides cause fatigue or lethargy?', a: "Fatigue during early use reflects the body redirecting energy toward tissue repair. TB-500's fatigue is specifically linked to systemic immune modulation. Most users normalize by week 2–3." },
  { q: 'Does CJC-1295 + Ipamorelin cause water retention?', a: 'Yes, mild water retention affects roughly 15–25% of users. It typically resolves after 2–4 weeks. Staying hydrated and timing administration before bed helps minimize it.' },
  { q: 'Can you mix peptides to reduce side effects?', a: 'Mixing peptides in the same syringe can increase risk. Each should be administered in a dedicated syringe. TB-500 + BPC-157 can be stacked but injected separately.' },
  { q: 'How do I reduce injection site reactions?', a: 'Rotate sites every dose. Use fresh needles. Ensure proper reconstitution with bacteriostatic water. Inject slowly at 45° for subcutaneous.' },
  { q: 'What side effects are red flags requiring medical attention?', a: 'Stop use for: severe allergic reactions, significant joint swelling, persistent numbness beyond 3 weeks, heart palpitations, or injection site infection signs.' },
  { q: 'Do I need bloodwork before starting peptides?', a: 'Yes — baseline bloodwork is strongly recommended, especially for GH-axis peptides. At minimum: IGF-1, metabolic panel, CBC, and relevant hormones.' },
  { q: 'How does Retatrutide compare to semaglutide for side effects?', a: 'Retatrutide has a higher rate of GI side effects during dose escalation (nausea ~45% vs ~30% for semaglutide). The glucagon receptor component causes more diarrhea. However, slow titration over 12–16 weeks significantly reduces severity. The tradeoff is substantially greater weight loss (24.2% vs ~15%).' },
  { q: 'Is Retatrutide safe for long-term use?', a: 'Long-term safety data is still being gathered in Phase 3 trials (as of 2026). Phase 2 data (48 weeks) showed no unexpected serious adverse events. Pancreatic enzyme elevations were observed but without clinical pancreatitis. Cardiovascular outcomes trials are ongoing.' },
];

const RELATED = [
  { title: 'How to Reconstitute Peptides', href: '/blogs/article/how-to-reconstitute-peptides' },
  { title: 'Why Mixing Peptides Sabotages Results', href: '/blogs/article/why-mixing-peptides-sabotages-results' },
  { title: 'Blood Tests Before Peptides: Complete Lab Guide', href: '/blogs/article/blood-tests-before-peptides-the-complete-pre-protocol-lab-guide' },
  { title: 'The Truth About Peptide Quality', href: '/blogs/article/the-truth-about-peptide-quality-why-99-purity-means-nothing-2026-guide' },
  { title: 'Peptides for Women: Dosing & Safety', href: '/blogs/article/peptides-for-women-dosing-cycle-timing-safety-guide' },
  { title: 'The Wolverine Complex: Recovery Stack', href: '/blogs/article/the-wolverine-complex-the-ultimate-peptide-stack-for-rapid-recovery-and-healing' },
  { title: 'Injection Site Reactions Guide', href: '/blogs/article/peptide-injection-site-reactions-redness-welts-whats-normal' },
  { title: 'AOD-9604: Fat-Burning Peptide Guide', href: '/blogs/article/aod-9604-fat-burning-peptide-guide' },
  { title: 'GHK-Cu Complete Masterclass', href: '/blogs/article/ghk-cu-copper-peptide-the-complete-master-class-guide-2026' },
];

const NAV_ITEMS = [
  { id: 'overview', label: 'Overview' },
  { id: 'bpc157', label: 'BPC-157' },
  { id: 'tb500', label: 'TB-500' },
  { id: 'ghkcu', label: 'GHK-Cu' },
  { id: 'cjc', label: 'CJC + Ipa' },
  { id: 'aod', label: 'AOD-9604' },
  { id: 'retatrutide', label: 'Retatrutide' },
  { id: 'compare', label: 'Compare' },
  { id: 'quiz', label: 'Quiz' },
  { id: 'why', label: 'Why They Happen' },
  { id: 'faq', label: 'FAQ' },
];

// ── COMPONENTS ──

function SeverityBar({ label, pct, red, visible }) {
  return (
    <div className="flex items-center gap-3 my-2">
      <span className="w-28 text-sm font-semibold text-white flex-shrink-0">{label}</span>
      <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-[1200ms] ease-out ${red ? 'bg-red-500' : 'bg-[#00F0FF]'}`}
          style={{ width: visible ? `${pct}%` : '0%' }} />
      </div>
      <span className="text-xs font-mono text-zinc-500 w-9 text-right">{pct}%</span>
    </div>
  );
}

function PeptideCard({ peptide, isOpen, onToggle }) {
  const cardRef = useRef(null);
  const [barsVisible, setBarsVisible] = useState(false);

  useEffect(() => {
    if (!isOpen || !peptide.severity) return;
    const timer = setTimeout(() => setBarsVisible(true), 200);
    return () => clearTimeout(timer);
  }, [isOpen, peptide.severity]);

  const nameParts = peptide.name.split(peptide.highlight);

  return (
    <div ref={cardRef} className="border border-zinc-800 my-8 overflow-hidden transition-shadow hover:shadow-lg hover:shadow-[#00F0FF]/5"
      data-testid={`peptide-card-${peptide.id}`}>
      <button onClick={onToggle}
        className="w-full bg-zinc-900 hover:bg-zinc-800/80 p-5 sm:p-6 flex items-center justify-between transition-colors cursor-pointer text-left"
        data-testid={`peptide-card-toggle-${peptide.id}`}>
        <div>
          <h3 className="font-heading text-2xl sm:text-3xl tracking-wide text-white">
            {nameParts[0]}<span className="text-[#00F0FF]">{peptide.highlight}</span>{nameParts[1] || ''}
          </h3>
          <p className="text-[10px] sm:text-xs font-mono tracking-widest uppercase text-zinc-500 mt-1">{peptide.label}</p>
        </div>
        <div className={`w-8 h-8 border border-zinc-700 rounded-full flex items-center justify-center text-[#00F0FF] flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDown className="w-4 h-4" />
        </div>
      </button>

      <div className={`transition-all duration-500 ease-in-out overflow-hidden ${isOpen ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="p-5 sm:p-7 space-y-6">
          {/* Reactions grid */}
          <div>
            <h4 className="text-[10px] font-mono tracking-widest uppercase text-zinc-500 mb-3">Reported Reactions</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {peptide.reactions.map((r, i) => (
                <div key={r.label} className={`p-3 rounded-sm ${r.rare ? 'bg-red-950/20 border-l-2 border-red-500' : 'bg-zinc-800/30 border-l-2 border-[#00F0FF]'}`}>
                  <span className={`text-[9px] font-mono tracking-widest uppercase ${r.rare ? 'text-red-400' : 'text-zinc-500'}`}>{r.label}</span>
                  <p className="text-sm text-zinc-300 mt-0.5 leading-relaxed">{r.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Citations */}
          {peptide.citations.map((c, i) => (
            <div key={c.text} className="flex gap-3 p-4 bg-[#00F0FF]/[0.03] border border-[#00F0FF]/10 rounded-sm">
              <FlaskConical className="w-4 h-4 text-[#00F0FF] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-zinc-300 leading-relaxed">{c.text}</p>
                <span className="text-[10px] font-mono text-zinc-600 mt-1 block">{c.source}</span>
              </div>
            </div>
          ))}

          {/* Severity bars */}
          {peptide.severity && (
            <div>
              <h4 className="text-[10px] font-mono tracking-widest uppercase text-zinc-500 mb-2">Severity Overview</h4>
              {peptide.severity.map((s, i) => (
                <SeverityBar key={s.label} {...s} visible={barsVisible} />
              ))}
              <p className="text-[10px] font-mono text-zinc-700 mt-2">* Approximate frequency based on available data. Not representative of all users.</p>
            </div>
          )}

          {/* Normal box */}
          <div className="bg-zinc-800/30 border border-zinc-700/50 p-4 rounded-sm">
            <p className="text-[10px] font-mono tracking-widest uppercase text-zinc-500 mb-2">{peptide.normal.title}</p>
            <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-line">{peptide.normal.text}</p>
          </div>

          {/* Extra note */}
          {peptide.extraNote && (
            <p className="text-sm text-zinc-400 leading-relaxed">
              <span className="px-1 py-0.5 bg-[#00F0FF]/10 text-[#00F0FF] text-xs rounded-sm">{peptide.extraNote.split(':')[0]}:</span>
              {peptide.extraNote.split(':').slice(1).join(':')}
            </p>
          )}

          {/* APR trust bar — persistent credibility band above affiliate CTA */}
          <AprTrustBlock variant="compact" peptide={peptide.name} />

          {/* CTA */}
          <div className="flex items-center justify-between gap-4 p-4 bg-zinc-900 border border-zinc-800 flex-wrap">
            <div className="min-w-0">
              <p className="text-white font-semibold text-sm">{peptide.cta.text}</p>
              <p className="text-xs text-zinc-500">{peptide.cta.sub}</p>
            </div>
            <a href={peptide.cta.url} target="_blank" rel="nofollow sponsored noreferrer"
              className="flex-shrink-0 px-5 py-2.5 bg-[#00F0FF] text-black text-xs font-mono font-bold uppercase tracking-wider hover:bg-[#00d6e0] transition-colors">
              {peptide.cta.btnText} <ExternalLink className="w-3 h-3 inline ml-1" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function FAQItem({ faq, isOpen, onToggle }) {
  return (
    <div className="border-b border-zinc-800" data-testid="faq-item">
      <button onClick={onToggle} className="w-full py-4 flex items-center justify-between text-left group cursor-pointer">
        <span className="text-sm sm:text-base font-semibold text-white group-hover:text-[#00F0FF] transition-colors pr-4">{faq.q}</span>
        <ChevronDown className={`w-4 h-4 text-[#00F0FF] flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`}>
        <p className="text-sm text-zinc-400 leading-relaxed">{faq.a}</p>
      </div>
    </div>
  );
}

function ComparePanel() {
  const [selected, setSelected] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const compare = params.get('compare');
    if (compare) {
      const ids = compare.split(',').filter(id => COMPARE_DATA[id]);
      return ids.slice(0, 3);
    }
    return [];
  });
  const [copied, setCopied] = useState(false);
  const autoScrolled = useRef(false);

  // Listen for quiz-compare events
  useEffect(() => {
    const handler = (e) => {
      if (e.detail && Array.isArray(e.detail)) {
        setSelected(e.detail.slice(0, 3));
      }
    };
    window.addEventListener('quiz-compare', handler);
    return () => window.removeEventListener('quiz-compare', handler);
  }, []);

  // Auto-scroll to compare section if URL has compare params
  useEffect(() => {
    if (selected.length >= 2 && !autoScrolled.current) {
      autoScrolled.current = true;
      setTimeout(() => {
        document.getElementById('pse-compare')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 600);
    }
  }, [selected.length]);

  // Update URL params when selection changes
  useEffect(() => {
    const url = new URL(window.location.href);
    if (selected.length > 0) {
      url.searchParams.set('compare', selected.join(','));
    } else {
      url.searchParams.delete('compare');
    }
    window.history.replaceState({}, '', url.toString());
  }, [selected]);

  const toggle = (id) => {
    setSelected(prev =>
      prev.includes(id)
        ? prev.filter(s => s !== id)
        : prev.length >= 3 ? prev : [...prev, id]
    );
    setCopied(false);
  };

  const handleCopyLink = async () => {
    const url = new URL(window.location.href);
    url.searchParams.set('compare', selected.join(','));
    url.hash = '';
    try {
      await navigator.clipboard.writeText(url.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback
      const input = document.createElement('input');
      input.value = url.toString();
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleDownloadPDF = () => {
    const data = selected.map(id => ({ ...COMPARE_DATA[id], id, name: PEPTIDES.find(p => p.id === id)?.name || id }));
    const rows = [
      { label: 'Category', fn: d => d.category },
      { label: 'Evidence Level', fn: d => `${d.evidenceLevel} — ${d.evidenceDetail}` },
      { label: 'Top Side Effects', fn: d => d.topEffects.join(', ') },
      { label: 'Overall Severity', fn: d => `${d.effectSeverity} (reported by ${d.effectRate})` },
      { label: 'Titration Protocol', fn: d => d.titration },
      { label: 'Adaptation Window', fn: d => d.adaptationWindow },
      { label: 'Key Risk', fn: d => d.keyRisk },
      { label: 'Best For', fn: d => d.bestFor },
    ];

    const colW = Math.floor(70 / data.length);
    const thCells = data.map(d => `<th style="width:${colW}%;padding:10px 14px;text-align:left;border-bottom:2px solid #00F0FF;color:#00F0FF;font-size:15px;font-weight:700;">${d.name}<br><span style="font-size:10px;color:#888;font-weight:400;">${d.category}</span></th>`).join('');
    const bodyRows = rows.map(r => {
      const cells = data.map(d => `<td style="padding:10px 14px;border-bottom:1px solid #333;color:#d4d4d8;font-size:12px;line-height:1.5;vertical-align:top;">${r.fn(d)}</td>`).join('');
      return `<tr><td style="padding:10px 14px;border-bottom:1px solid #333;color:#888;font-size:10px;text-transform:uppercase;letter-spacing:1.5px;font-weight:600;width:30%;vertical-align:top;">${r.label}</td>${cells}</tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><title>Peptide Comparison — NinjAthlete</title>
<style>@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}</style>
</head><body style="margin:0;padding:40px;background:#0a0a0a;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#fff;">
<div style="max-width:900px;margin:0 auto;">
  <div style="margin-bottom:30px;">
    <div style="color:#00F0FF;font-size:10px;text-transform:uppercase;letter-spacing:2px;font-weight:700;">NinjAthlete</div>
    <h1 style="font-size:26px;margin:6px 0;color:#fff;">Peptide Side Effect Comparison</h1>
    <p style="font-size:12px;color:#888;margin:0;">${data.map(d=>d.name).join(' vs ')} — Generated ${new Date().toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}</p>
  </div>
  <table style="width:100%;border-collapse:collapse;background:#111;">
    <thead><tr><th style="width:30%;padding:10px 14px;border-bottom:2px solid #333;"></th>${thCells}</tr></thead>
    <tbody>${bodyRows}</tbody>
  </table>
  <p style="font-size:9px;color:#555;margin-top:16px;">Comparison based on available clinical data. Individual responses vary. This is not medical advice. Consult a healthcare professional.</p>
  <p style="font-size:9px;color:#555;">Source: ninjathlete.com/blogs/article/peptide-side-effects-what-the-research-actually-says</p>
</div>
</body></html>`;

    const win = window.open('', '_blank', 'width=960,height=700');
    if (win) {
      win.document.write(html);
      win.document.close();
      setTimeout(() => win.print(), 400);
    }
  };

  const selectedData = selected.map(id => ({ ...COMPARE_DATA[id], id, name: PEPTIDES.find(p => p.id === id)?.name || id }));

  return (
    <section id="pse-compare" className="mt-20 mb-16 scroll-mt-16" data-testid="compare-panel">
      <div className="flex items-center gap-3 mb-2">
        <GitCompareArrows className="w-6 h-6 text-[#00F0FF]" />
        <h2 className="font-heading text-3xl sm:text-4xl text-white">COMPARE PEPTIDES</h2>
      </div>
      <p className="text-base text-zinc-400 leading-relaxed mb-6">
        Select 2-3 peptides to see their side effect profiles side by side.
      </p>

      {/* Selector pills */}
      <div className="flex flex-wrap gap-2 mb-8" data-testid="compare-selector">
        {PEPTIDES.map(p => {
          const isActive = selected.includes(p.id);
          const isDisabled = !isActive && selected.length >= 3;
          return (
            <button key={p.id} onClick={() => !isDisabled && toggle(p.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-mono tracking-wider uppercase border transition-all ${
                isActive
                  ? 'bg-[#00F0FF]/10 border-[#00F0FF]/40 text-[#00F0FF]'
                  : isDisabled
                    ? 'bg-zinc-900/20 border-zinc-800/30 text-zinc-700 cursor-not-allowed'
                    : 'bg-zinc-900/40 border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200'
              }`}
              data-testid={`compare-pill-${p.id}`}>
              {isActive ? <Check className="w-3 h-3" /> : null}
              {p.name}
            </button>
          );
        })}
      </div>

      {selected.length < 2 && (
        <div className="text-center py-12 border border-dashed border-zinc-800 text-zinc-600 text-sm" data-testid="compare-empty">
          Select at least 2 peptides above to compare
        </div>
      )}

      {/* Comparison table */}
      {selected.length >= 2 && (
        <div className="overflow-x-auto" data-testid="compare-table">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="text-left text-[10px] font-mono tracking-widest uppercase text-zinc-600 p-3 border-b border-zinc-800 w-36 sticky left-0 bg-[#0a0a0a]"></th>
                {selectedData.map(d => (
                  <th key={d.id} className="text-left p-3 border-b border-zinc-800 min-w-[200px]">
                    <span className="text-white font-heading text-lg">{d.name}</span>
                    <br /><span className="text-[10px] font-mono text-zinc-500 tracking-wider">{d.category}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Evidence */}
              <tr>
                <td className="p-3 text-[10px] font-mono tracking-widest uppercase text-zinc-500 border-b border-zinc-800/50 sticky left-0 bg-[#0a0a0a]">Evidence</td>
                {selectedData.map(d => (
                  <td key={d.id} className="p-3 border-b border-zinc-800/50">
                    <span className="text-xs font-semibold px-2 py-0.5 border" style={{ color: EVIDENCE_COLORS[d.evidenceLevel] || '#71717a', borderColor: `${EVIDENCE_COLORS[d.evidenceLevel] || '#71717a'}30` }}>
                      {d.evidenceLevel}
                    </span>
                    <p className="text-[11px] text-zinc-500 mt-1.5 leading-relaxed">{d.evidenceDetail}</p>
                  </td>
                ))}
              </tr>
              {/* Top Side Effects */}
              <tr>
                <td className="p-3 text-[10px] font-mono tracking-widest uppercase text-zinc-500 border-b border-zinc-800/50 sticky left-0 bg-[#0a0a0a]">Top Effects</td>
                {selectedData.map(d => (
                  <td key={d.id} className="p-3 border-b border-zinc-800/50">
                    <ul className="space-y-1">
                      {d.topEffects.map((e, i) => (
                        <li key={e} className="text-xs text-zinc-300 flex items-start gap-1.5">
                          <span className="text-[#00F0FF] mt-0.5 text-[8px]">&#9679;</span>{e}
                        </li>
                      ))}
                    </ul>
                  </td>
                ))}
              </tr>
              {/* Severity */}
              <tr>
                <td className="p-3 text-[10px] font-mono tracking-widest uppercase text-zinc-500 border-b border-zinc-800/50 sticky left-0 bg-[#0a0a0a]">Severity</td>
                {selectedData.map(d => {
                  const sev = SEVERITY_RANK[d.effectSeverity] || SEVERITY_RANK['Moderate'];
                  return (
                    <td key={d.id} className="p-3 border-b border-zinc-800/50">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold" style={{ color: sev.color }}>{d.effectSeverity}</span>
                      </div>
                      <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${sev.pct}%`, backgroundColor: sev.color }} />
                      </div>
                      <p className="text-[10px] text-zinc-600 mt-1">Reported by {d.effectRate} of users</p>
                    </td>
                  );
                })}
              </tr>
              {/* Titration */}
              <tr>
                <td className="p-3 text-[10px] font-mono tracking-widest uppercase text-zinc-500 border-b border-zinc-800/50 sticky left-0 bg-[#0a0a0a]">Titration</td>
                {selectedData.map(d => (
                  <td key={d.id} className="p-3 border-b border-zinc-800/50">
                    <p className="text-xs text-zinc-300 leading-relaxed">{d.titration}</p>
                  </td>
                ))}
              </tr>
              {/* Adaptation Window */}
              <tr>
                <td className="p-3 text-[10px] font-mono tracking-widest uppercase text-zinc-500 border-b border-zinc-800/50 sticky left-0 bg-[#0a0a0a]">Adaptation</td>
                {selectedData.map(d => (
                  <td key={d.id} className="p-3 border-b border-zinc-800/50">
                    <span className="text-xs text-zinc-300">{d.adaptationWindow}</span>
                  </td>
                ))}
              </tr>
              {/* Key Risk */}
              <tr>
                <td className="p-3 text-[10px] font-mono tracking-widest uppercase text-zinc-500 border-b border-zinc-800/50 sticky left-0 bg-[#0a0a0a]">Key Risk</td>
                {selectedData.map(d => (
                  <td key={d.id} className="p-3 border-b border-zinc-800/50">
                    <p className="text-xs text-zinc-400 leading-relaxed">{d.keyRisk}</p>
                  </td>
                ))}
              </tr>
              {/* Best For */}
              <tr>
                <td className="p-3 text-[10px] font-mono tracking-widest uppercase text-zinc-500 sticky left-0 bg-[#0a0a0a]">Best For</td>
                {selectedData.map(d => (
                  <td key={d.id} className="p-3">
                    <span className="text-xs text-[#00F0FF]">{d.bestFor}</span>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {selected.length >= 2 && (
        <div className="flex flex-wrap items-center gap-3 mt-5" data-testid="compare-actions">
          <button onClick={handleCopyLink}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-mono tracking-wider uppercase border transition-all ${
              copied
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                : 'bg-zinc-900/40 border-zinc-700 text-zinc-300 hover:border-[#00F0FF]/40 hover:text-[#00F0FF]'
            }`}
            data-testid="compare-copy-link">
            {copied ? <ClipboardCheck className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
            {copied ? 'Link Copied!' : 'Copy Shareable Link'}
          </button>
          <button onClick={handleDownloadPDF}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-mono tracking-wider uppercase bg-zinc-900/40 border border-zinc-700 text-zinc-300 hover:border-[#00F0FF]/40 hover:text-[#00F0FF] transition-all"
            data-testid="compare-download-pdf">
            <Download className="w-3.5 h-3.5" /> Save as PDF
          </button>
        </div>
      )}

      {selected.length >= 2 && (
        <p className="text-[9px] text-zinc-700 mt-4 flex items-center gap-1">
          <Shield className="w-2.5 h-2.5" /> Comparison based on available clinical data. Individual responses vary. Not medical advice.
        </p>
      )}
    </section>
  );
}

// ── QUIZ DATA ──
const QUIZ_QUESTIONS = [
  {
    id: 'goal',
    question: "What's your primary goal?",
    options: [
      { id: 'recovery', label: 'Injury recovery / tissue repair', icon: '🔧' },
      { id: 'fatloss', label: 'Fat loss / body composition', icon: '🔥' },
      { id: 'antiaging', label: 'Anti-aging / longevity', icon: '⏳' },
      { id: 'gh', label: 'Growth hormone / performance', icon: '📈' },
      { id: 'gut', label: 'Gut healing / inflammation', icon: '🛡' },
    ],
  },
  {
    id: 'experience',
    question: 'What is your experience with peptides?',
    options: [
      { id: 'none', label: 'Complete beginner — never used peptides', icon: '🆕' },
      { id: 'some', label: 'Some research, no injections yet', icon: '📚' },
      { id: 'moderate', label: 'Used 1–2 peptides before', icon: '💉' },
      { id: 'advanced', label: 'Experienced — multiple protocols', icon: '🧬' },
    ],
  },
  {
    id: 'tolerance',
    question: 'How would you describe your side-effect tolerance?',
    options: [
      { id: 'low', label: 'Low — I want the mildest option possible', icon: '🕊' },
      { id: 'medium', label: 'Medium — I can handle temporary mild effects', icon: '⚖' },
      { id: 'high', label: 'High — effectiveness matters more than comfort', icon: '💪' },
    ],
  },
  {
    id: 'delivery',
    question: 'Preferred delivery method?',
    options: [
      { id: 'topical', label: 'Topical (cream/serum) — no needles', icon: '🧴' },
      { id: 'subq', label: 'Subcutaneous injection — fine with needles', icon: '💉' },
      { id: 'oral', label: 'Oral (capsule) — if available', icon: '💊' },
      { id: 'any', label: 'Whatever works best', icon: '✅' },
    ],
  },
  {
    id: 'timeline',
    question: 'How soon do you need results?',
    options: [
      { id: 'fast', label: '1–4 weeks — dealing with an active injury or event', icon: '⚡' },
      { id: 'medium', label: '1–3 months — steady improvement', icon: '📅' },
      { id: 'long', label: '3+ months — long-term health investment', icon: '🎯' },
    ],
  },
];

const QUIZ_RESULTS = {
  bpc157: { name: 'BPC-157', tagline: 'The gold standard for targeted tissue repair and gut healing', why: 'BPC-157 has the mildest side effect profile of any repair peptide, extensive preclinical backing, and works within 1–4 weeks for most injuries. Ideal for beginners due to its safety margin.' },
  tb500: { name: 'TB-500', tagline: 'Systemic repair for widespread soft tissue recovery', why: 'TB-500 travels systemically rather than acting locally, making it ideal for multiple injury sites. Expect mild fatigue during the 2–3 week loading phase — a sign it\'s working.' },
  ghkcu: { name: 'GHK-Cu', tagline: 'The anti-aging peptide with the gentlest profile', why: 'GHK-Cu is naturally found in your body. Topical application requires no injections and has a near-zero side effect rate. It modulates 4,000+ genes related to collagen, inflammation, and tissue repair.' },
  cjc: { name: 'CJC-1295 + Ipamorelin', tagline: 'Clinical-grade growth hormone optimization', why: 'The most prescribed GH peptide stack in longevity medicine. Proven in RCTs to raise GH 2–10x. Expect mild water retention and tingling during week 1–2 that resolves with continued use.' },
  aod: { name: 'AOD-9604', tagline: 'The safest fat loss peptide — tolerability equals placebo', why: 'Six human RCTs with 893+ participants found AOD-9604\'s side effect profile indistinguishable from placebo. Targets fat cells directly without affecting IGF-1 or blood sugar. GRAS designated.' },
  retatrutide: { name: 'Retatrutide', tagline: 'Maximum fat loss via triple receptor agonism', why: 'The most potent weight loss compound studied to date (24.2% at 48 weeks). Higher GI side effects than other options, but slow titration over 12–16 weeks makes it manageable. For experienced users who prioritize results.' },
};

function scoreQuiz(answers) {
  const scores = { bpc157: 0, tb500: 0, ghkcu: 0, cjc: 0, aod: 0, retatrutide: 0 };

  // Goal
  const g = answers.goal;
  if (g === 'recovery') { scores.bpc157 += 5; scores.tb500 += 4; }
  if (g === 'fatloss') { scores.aod += 5; scores.retatrutide += 4; scores.cjc += 2; }
  if (g === 'antiaging') { scores.ghkcu += 5; scores.bpc157 += 2; scores.cjc += 2; }
  if (g === 'gh') { scores.cjc += 5; scores.tb500 += 2; }
  if (g === 'gut') { scores.bpc157 += 6; }

  // Experience
  const e = answers.experience;
  if (e === 'none') { scores.bpc157 += 3; scores.ghkcu += 3; scores.aod += 2; }
  if (e === 'some') { scores.bpc157 += 2; scores.aod += 2; scores.tb500 += 1; }
  if (e === 'moderate') { scores.cjc += 2; scores.tb500 += 2; scores.retatrutide += 1; }
  if (e === 'advanced') { scores.retatrutide += 3; scores.cjc += 2; }

  // Tolerance
  const t = answers.tolerance;
  if (t === 'low') { scores.aod += 4; scores.ghkcu += 3; scores.bpc157 += 3; scores.retatrutide -= 2; }
  if (t === 'medium') { scores.tb500 += 2; scores.cjc += 2; scores.bpc157 += 1; }
  if (t === 'high') { scores.retatrutide += 3; scores.cjc += 2; scores.tb500 += 1; }

  // Delivery
  const d = answers.delivery;
  if (d === 'topical') { scores.ghkcu += 5; scores.bpc157 -= 1; scores.tb500 -= 1; }
  if (d === 'oral') { scores.bpc157 += 2; scores.ghkcu += 1; }
  if (d === 'subq' || d === 'any') { /* no penalty */ }

  // Timeline
  const tl = answers.timeline;
  if (tl === 'fast') { scores.bpc157 += 3; scores.tb500 += 2; }
  if (tl === 'medium') { scores.cjc += 2; scores.aod += 2; scores.tb500 += 1; }
  if (tl === 'long') { scores.ghkcu += 3; scores.retatrutide += 2; scores.cjc += 1; }

  // Sort by score
  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  return { primary: sorted[0][0], secondary: sorted[1][0], scores: Object.fromEntries(sorted) };
}

function DecisionHelperQuiz() {
  const [step, setStep] = useState(0); // 0-4 = questions, 5 = results
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [animateIn, setAnimateIn] = useState(true);

  const currentQ = QUIZ_QUESTIONS[step];
  const progress = step < 5 ? ((step + 1) / 5) * 100 : 100;

  const selectAnswer = (optionId) => {
    const newAnswers = { ...answers, [currentQ.id]: optionId };
    setAnswers(newAnswers);
    setAnimateIn(false);

    setTimeout(() => {
      if (step < 4) {
        setStep(step + 1);
      } else {
        setResult(scoreQuiz(newAnswers));
        setStep(5);
      }
      setAnimateIn(true);
    }, 300);
  };

  const restart = () => {
    setStep(0);
    setAnswers({});
    setResult(null);
    setAnimateIn(true);
  };

  const primary = result ? QUIZ_RESULTS[result.primary] : null;
  const secondary = result ? QUIZ_RESULTS[result.secondary] : null;
  const compareUrl = result ? `?compare=${result.primary},${result.secondary}` : '';

  return (
    <section id="pse-quiz" className="mt-20 mb-16 scroll-mt-16" data-testid="quiz-section">
      <div className="border border-zinc-800 overflow-hidden">
        {/* Header */}
        <div className="bg-zinc-900 p-5 sm:p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3 mb-1">
            <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#00F0FF] bg-[#00F0FF]/10 border border-[#00F0FF]/20 px-2 py-0.5">Interactive</span>
            <span className="text-[10px] font-mono tracking-widest uppercase text-zinc-600">
              {step < 5 ? `Question ${step + 1} of 5` : 'Results'}
            </span>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl text-white">PEPTIDE DECISION HELPER</h2>
          <p className="text-xs text-zinc-500 mt-1">5 questions to find your ideal starting peptide based on goals, experience, and preferences.</p>

          {/* Progress bar */}
          <div className="mt-4 h-1 bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full bg-[#00F0FF] rounded-full transition-all duration-500 ease-out" style={{ width: `${progress}%` }} data-testid="quiz-progress" />
          </div>
        </div>

        {/* Questions */}
        {step < 5 && (
          <div className={`p-5 sm:p-7 transition-all duration-300 ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
            <h3 className="text-lg sm:text-xl text-white font-semibold mb-5" data-testid="quiz-question">{currentQ.question}</h3>
            <div className="grid gap-2">
              {currentQ.options.map(opt => (
                <button key={opt.id} onClick={() => selectAnswer(opt.id)}
                  className="flex items-center gap-3 p-4 bg-zinc-900/30 border border-zinc-800 hover:border-[#00F0FF]/30 hover:bg-[#00F0FF]/[0.03] transition-all text-left group"
                  data-testid={`quiz-option-${opt.id}`}>
                  <span className="text-lg flex-shrink-0 w-8 text-center">{opt.icon}</span>
                  <span className="text-sm text-zinc-300 group-hover:text-white transition-colors">{opt.label}</span>
                  <ArrowUp className="w-3.5 h-3.5 text-zinc-700 group-hover:text-[#00F0FF] ml-auto rotate-90 transition-colors flex-shrink-0" />
                </button>
              ))}
            </div>

            {step > 0 && (
              <button onClick={() => { setAnimateIn(false); setTimeout(() => { setStep(step - 1); setAnimateIn(true); }, 200); }}
                className="mt-4 text-[10px] font-mono text-zinc-600 hover:text-zinc-300 transition uppercase tracking-wider"
                data-testid="quiz-back">
                &#8592; Back
              </button>
            )}
          </div>
        )}

        {/* Results */}
        {step === 5 && result && (
          <div className={`p-5 sm:p-7 transition-all duration-500 ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-testid="quiz-results">
            {/* Primary recommendation */}
            <div className="mb-6">
              <span className="text-[10px] font-mono tracking-widest uppercase text-[#00F0FF] mb-2 block">Top Recommendation</span>
              <div className="p-5 bg-[#00F0FF]/[0.04] border border-[#00F0FF]/20">
                <h3 className="font-heading text-2xl sm:text-3xl text-[#00F0FF] mb-1" data-testid="quiz-primary-result">{primary.name}</h3>
                <p className="text-sm font-semibold text-white mb-3">{primary.tagline}</p>
                <p className="text-sm text-zinc-400 leading-relaxed">{primary.why}</p>
              </div>
            </div>

            {/* Runner-up */}
            <div className="mb-6">
              <span className="text-[10px] font-mono tracking-widest uppercase text-zinc-600 mb-2 block">Also Consider</span>
              <div className="p-4 bg-zinc-900/30 border border-zinc-800">
                <h4 className="text-lg text-white font-semibold mb-1" data-testid="quiz-secondary-result">{secondary.name}</h4>
                <p className="text-xs text-zinc-500">{secondary.tagline}</p>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 mb-5" data-testid="quiz-actions">
              <a href={`/blogs/article/peptide-side-effects-what-the-research-actually-says${compareUrl}`}
                onClick={(e) => {
                  e.preventDefault();
                  const url = new URL(window.location.href);
                  url.searchParams.set('compare', `${result.primary},${result.secondary}`);
                  window.history.replaceState({}, '', url.toString());
                  document.getElementById('pse-compare')?.scrollIntoView({ behavior: 'smooth' });
                  // Trigger compare selection
                  window.dispatchEvent(new CustomEvent('quiz-compare', { detail: [result.primary, result.secondary] }));
                }}
                className="flex items-center gap-1.5 px-5 py-2.5 bg-[#00F0FF] text-black text-xs font-mono font-bold uppercase tracking-wider hover:bg-[#00d6e0] transition-colors"
                data-testid="quiz-compare-link">
                <GitCompareArrows className="w-3.5 h-3.5" /> Compare These Two
              </a>
              <Link to="/tools/peptide-stack"
                className="flex items-center gap-1.5 px-5 py-2.5 bg-zinc-900 border border-zinc-700 text-zinc-300 text-xs font-mono font-bold uppercase tracking-wider hover:border-[#00F0FF]/40 hover:text-[#00F0FF] transition-colors"
                data-testid="quiz-stack-link">
                Build a Stack
              </Link>
            </div>

            {/* Score breakdown */}
            <details className="group" data-testid="quiz-scores">
              <summary className="text-[10px] font-mono text-zinc-600 hover:text-zinc-400 cursor-pointer uppercase tracking-wider transition">
                View all scores &#9662;
              </summary>
              <div className="mt-3 space-y-1.5">
                {Object.entries(result.scores).map(([id, score]) => {
                  const maxScore = Object.values(result.scores)[0];
                  const pct = maxScore > 0 ? (score / maxScore) * 100 : 0;
                  const isTop = id === result.primary;
                  return (
                    <div key={id} className="flex items-center gap-3">
                      <span className={`w-28 text-xs font-mono flex-shrink-0 ${isTop ? 'text-[#00F0FF] font-semibold' : 'text-zinc-500'}`}>
                        {QUIZ_RESULTS[id]?.name || id}
                      </span>
                      <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-700 ${isTop ? 'bg-[#00F0FF]' : 'bg-zinc-600'}`}
                          style={{ width: `${Math.max(pct, 3)}%` }} />
                      </div>
                      <span className="text-[10px] font-mono text-zinc-600 w-6 text-right">{score}</span>
                    </div>
                  );
                })}
              </div>
            </details>

            <button onClick={restart}
              className="mt-5 text-[10px] font-mono text-zinc-600 hover:text-[#00F0FF] transition uppercase tracking-wider"
              data-testid="quiz-restart">
              &#8634; Retake Quiz
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

// ── MAIN ──
export default function PeptideSideEffectsArticle() {
  const [activeNav, setActiveNav] = useState('overview');
  const [openCards, setOpenCards] = useState({ bpc157: true });
  const [openFaq, setOpenFaq] = useState(0);
  const [progress, setProgress] = useState(0);
  const [showTop, setShowTop] = useState(false);
  const contentRef = useRef(null);

  const handleScroll = useCallback(() => {
    const h = document.documentElement;
    const pct = Math.min((h.scrollTop / (h.scrollHeight - h.clientHeight)) * 100, 100);
    setProgress(pct);
    setShowTop(h.scrollTop > 500);
    setProgressForSlug(ARTICLE_SLUG, pct);

    // Update active nav
    const offsets = NAV_ITEMS.map(n => {
      const el = document.getElementById(`pse-${n.id}`);
      return el ? { id: n.id, top: el.getBoundingClientRect().top } : null;
    }).filter(Boolean);
    const current = offsets.filter(o => o.top < 150).pop();
    if (current) setActiveNav(current.id);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollTo = (id) => {
    const el = document.getElementById(`pse-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="peptide-side-effects-article">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 h-[3px] bg-[#00F0FF] z-[100] transition-all duration-100" style={{ width: `${progress}%` }} />

      {/* Hero */}
      <section className="relative pt-28 pb-16 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_80%_50%,rgba(0,240,255,0.06)_0%,transparent_70%)]" />
        <div className="max-w-3xl mx-auto relative z-10">
          <span className="inline-block text-[10px] font-mono tracking-[0.12em] uppercase text-[#00F0FF] border border-[#00F0FF]/30 px-3 py-1 mb-6">
            Evidence-Based · Research Review · 2026
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-white mb-6" data-testid="article-title">
            PEPTIDE SIDE EFFECTS:<br/><span className="text-[#00F0FF]">WHAT THE RESEARCH ACTUALLY SAYS</span>
          </h1>
          <p className="text-lg text-zinc-300 max-w-xl leading-relaxed mb-8">
            BPC-157, TB-500, GHK-Cu, CJC-1295 + Ipamorelin, AOD-9604, and Retatrutide — not a fear piece, not a sales pitch. Just what the science documents, what's normal, and when you should actually be concerned.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs font-mono text-zinc-400">
            <span>March 2026</span>
            <span>12 min read</span>
            <span>Clinical citations included</span>
            <span>NinjAthlete</span>
          </div>
        </div>
      </section>

      {/* Sticky Nav */}
      <nav className="sticky top-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-zinc-800/50" data-testid="article-sticky-nav">
        <div className="max-w-3xl mx-auto flex overflow-x-auto scrollbar-hide px-4">
          {NAV_ITEMS.map(n => (
            <button key={n.id} onClick={() => scrollTo(n.id)}
              className={`whitespace-nowrap px-4 py-3 text-[10px] sm:text-[11px] font-mono tracking-wider uppercase border-b-2 transition-colors flex-shrink-0 ${activeNav === n.id ? 'text-[#00F0FF] border-[#00F0FF]' : 'text-zinc-500 border-transparent hover:text-zinc-300'}`}>
              {n.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Body */}
      <main ref={contentRef} className="max-w-3xl mx-auto px-6 py-14 sm:py-20">
        {/* Intro */}
        <section id="pse-overview" className="mb-16 animate-fadeIn">
          <div className="border-l-2 border-[#00F0FF] pl-6 mb-10">
            <p className="text-lg text-zinc-300 leading-relaxed">
              Every peptide has a list of "possible side effects." Most are copied from forums or glossed over by vendors. This guide is different: we went to the actual clinical research and built a clear picture of what these compounds <em>actually</em> do to the body during adaptation.
            </p>
          </div>
          <p className="text-base text-zinc-400 leading-relaxed mb-8">
            The six peptides covered here represent the most commonly used research peptides in the performance, longevity, recovery, and body composition space. Each one has a distinct mechanism of action, and each one has a distinct side effect profile.
          </p>

          {/* Stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
            {[
              { num: '6', title: 'Peptides Covered', sub: 'Each reviewed against clinical literature' },
              { num: '1,200+', title: 'Clinical Trial Subjects', sub: 'AOD-9604 (893) + Retatrutide (338)' },
              { num: '90%', title: 'Reactions Are Mild', sub: 'Dose-related and transient' },
              { num: '≠', title: 'Not All "Side Effects" Are Bad', sub: 'Many signal the peptide is working' },
            ].map((s, i) => (
              <div key={s.title} className="border border-zinc-800 p-4 group hover:border-[#00F0FF]/20 transition-colors">
                <p className="text-3xl sm:text-4xl font-heading text-[#00F0FF] leading-none mb-2">{s.num}</p>
                <p className="text-xs font-semibold text-white mb-1">{s.title}</p>
                <p className="text-[10px] text-zinc-500 leading-tight">{s.sub}</p>
              </div>
            ))}
          </div>

          <p className="text-sm text-zinc-400 leading-relaxed">
            Before diving in: none of these compounds are FDA-approved for human therapeutic use. This article is educational. If you're considering any peptide protocol, get baseline bloodwork first —{' '}
            <Link to="/blogs/article/blood-tests-before-peptides-the-complete-pre-protocol-lab-guide" className="text-[#00F0FF] hover:underline">
              read our pre-protocol lab guide
            </Link>.
          </p>
        </section>

        {/* Peptide Sections */}
        {PEPTIDES.map((p) => (
          <section key={p.id} id={`pse-${p.id}`} className="scroll-mt-16">
            <h2 className="font-heading text-3xl sm:text-4xl text-white mb-3 mt-14">{p.name}</h2>
            <p className="text-base text-zinc-400 leading-relaxed mb-2">{p.intro}</p>
            <PeptideCard
              peptide={p}
              isOpen={!!openCards[p.id]}
              onToggle={() => setOpenCards(prev => ({ ...prev, [p.id]: !prev[p.id] }))}
            />
          </section>
        ))}

        {/* Compare Peptides */}
        <ComparePanel />

        {/* Decision Helper Quiz */}
        <DecisionHelperQuiz />

        {/* Why Section */}
        <section id="pse-why" className="mt-20 mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">WHY SIDE EFFECTS HAPPEN</h2>
          <p className="text-base text-zinc-400 leading-relaxed mb-8">
            Most peptide reactions aren't random — they trace back to one of five identifiable root causes.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {WHY_REASONS.map((r, i) => (
              <div key={r.title} className="border border-zinc-800 p-5 hover:border-[#00F0FF]/20 transition-colors group">
                <p className="text-3xl font-heading text-[#00F0FF] mb-2">{r.num}</p>
                <p className="text-sm font-semibold text-white mb-1">{r.title}</p>
                <p className="text-xs text-zinc-500 leading-relaxed">{r.text}</p>
              </div>
            ))}
          </div>

          <div className="flex gap-3 p-4 bg-[#00F0FF]/[0.03] border border-[#00F0FF]/10 mt-8">
            <FlaskConical className="w-4 h-4 text-[#00F0FF] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-zinc-300 leading-relaxed">
              Peptides are <strong className="text-white">signaling molecules</strong> — not drugs in the traditional sense. The adaptation window for most research peptides is 1–3 weeks. After that, most mild initial reactions resolve.
            </p>
          </div>
        </section>

        {/* Related Links */}
        <div className="bg-zinc-900/30 border border-zinc-800/50 p-6 mb-16">
          <p className="text-[10px] font-mono tracking-widest uppercase text-zinc-500 mb-4">Continue Your Research</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {RELATED.map((r, i) => (
              <Link key={r.title} to={r.href} className="flex items-center gap-2 text-sm text-zinc-300 hover:text-[#00F0FF] transition-colors py-1">
                <span className="text-[#00F0FF] text-xs">&#8594;</span> {r.title}
              </Link>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <section id="pse-faq" className="mb-16 scroll-mt-16">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">FREQUENTLY ASKED QUESTIONS</h2>
          <div>
            {FAQS.map((f, i) => (
              <FAQItem key={f.q} faq={f} isOpen={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>
        </section>

        <RelatedGuides currentSlug={ARTICLE_SLUG} />

        {/* Disclaimer */}
        <div className="bg-amber-950/20 border border-amber-700/30 p-4 text-xs text-amber-200/70 leading-relaxed">
          <strong className="text-amber-200">Research Purposes Only:</strong> This article is for educational purposes only and does not constitute medical advice. The peptides discussed are research compounds not approved by the FDA for human therapeutic use. Consult a healthcare provider before considering any peptide protocol.
        </div>
      </main>

      {/* Scroll to top */}
      {showTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 w-10 h-10 bg-zinc-900 border border-zinc-700 rounded-full flex items-center justify-center text-white hover:bg-[#00F0FF] hover:text-black transition-colors z-50"
          data-testid="scroll-to-top">
          <ArrowUp className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
